// ═══ SUMAR AI TAB ════════════════════════════════════════════════════════════
// Sumarul general/săptămânal/anual generat de AI, plus secțiunea de sumar săptămânal.
import React, { useState } from "react";
import { t } from './i18n';
import { fmAlert } from './dialog';
import { isoD, toText, asStrArr, escapeHtml } from './helpers';
import { DateField, Dropdown } from './ui';

export function WeeklySummarySection({weeklySummary, onGenerate, processing, onExport}) {
  const [periodType, setPeriodType] = useState("current"); // current, last4, custom
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  // Calculate period dates based on selection
  function getPeriodDates() {
    const now = new Date();
    if(periodType==="current") {
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - now.getDay() + (now.getDay()===0?-6:1));
      weekStart.setHours(0,0,0,0);
      const prevWeekStart = new Date(weekStart); prevWeekStart.setDate(prevWeekStart.getDate()-7);
      return {
        start: isoD(weekStart),
        end: isoD(now),
        prevStart: isoD(prevWeekStart),
        prevEnd: isoD(new Date(weekStart.getTime()-86400000)),
        label: "Săptămâna curentă"
      };
    } else if(periodType==="last4") {
      const end = isoD(now);
      const start = new Date(now); start.setDate(now.getDate()-28);
      const prevStart = new Date(start); prevStart.setDate(prevStart.getDate()-28);
      return {
        start: isoD(start),
        end,
        prevStart: isoD(prevStart),
        prevEnd: isoD(new Date(start.getTime()-86400000)),
        label: "Ultimele 4 săptămâni"
      };
    } else {
      if(!customStart||!customEnd) return null;
      const diff = new Date(customStart)-new Date(customEnd);
      const prevEnd = isoD(new Date(new Date(customStart).getTime()-86400000));
      const prevStart = isoD(new Date(new Date(customStart).getTime()+diff));
      return {
        start: customStart, end: customEnd,
        prevStart, prevEnd,
        label: `${customStart} — ${customEnd}`
      };
    }
  }

  const periodDates = getPeriodDates();

  return (
    <div>
      {/* Period selector */}
      <div className="card" style={{marginBottom:14}}>
        <div className="lbl" style={{marginBottom:10}}>{t("Perioadă analiză")}</div>
        <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap"}}>
          {[["current","Săptămâna curentă"],["last4","Ultimele 4 săptămâni"],["custom","Perioadă personalizată"]].map(([v,l])=>(
            <button key={v} onClick={()=>setPeriodType(v)} style={{
              background:periodType===v?"#7c3aed":"var(--border)",
              border:`1px solid ${periodType===v?"#7c3aed":"var(--border-2)"}`,
              color:periodType===v?"#fff":"var(--text-2)",
              borderRadius:8,padding:"5px 12px",cursor:"pointer",
              fontSize:12,fontFamily:"inherit"
            }}>{t(l)}</button>
          ))}
        </div>
        {periodType==="custom"&&(
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <div><div className="lbl" style={{fontSize:10}}>{t("De la")}</div><DateField value={customStart} onChange={v=>setCustomStart(v)}/></div>
            <div><div className="lbl" style={{fontSize:10}}>{t("Până la")}</div><DateField value={customEnd} onChange={v=>setCustomEnd(v)}/></div>
          </div>
        )}
        {periodDates&&(
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:10}}>
            <div style={{fontSize:11,color:"var(--text-3)"}}>
              📅 {t(periodDates.label)} · {t("Comparat cu:")} {periodDates.prevStart} — {periodDates.prevEnd}
            </div>
            <button className="bai" onClick={()=>onGenerate(periodDates)} disabled={processing||!periodDates}>
              📅 {processing?t("Generez..."):t("Generează")}
            </button>
          </div>
        )}
      </div>

      {/* Results */}
      {!weeklySummary?(
        <div className="card" style={{textAlign:"center",color:"var(--text-4)",padding:"40px 0"}}>
          <div style={{fontSize:40,marginBottom:8}}>📅</div>
          <div>{t("Selectează perioada și apasă „Generează”.")}</div>
        </div>
      ):(
        <div>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12,flexWrap:"wrap"}}>
            <div style={{fontSize:11,color:"var(--text-4)"}}>{weeklySummary.period} · {t("Generat:")} {weeklySummary.generatedAt}</div>
            <button className="bg" style={{fontSize:10,padding:"4px 8px"}} onClick={()=>onExport&&onExport(t("Sumar")+" "+(weeklySummary.period||t("săptămânal")),weeklySummary)}>{t("⬇ PDF")}</button>
            {weeklySummary.overallStatus&&(
              <span className="pill" style={{
                background:weeklySummary.overallStatus==="bun"?"var(--green-bg-soft)":weeklySummary.overallStatus==="ingrijorator"?"var(--red-bg-soft)":"var(--amber-bg)",
                color:weeklySummary.overallStatus==="bun"?"var(--green)":weeklySummary.overallStatus==="ingrijorator"?"var(--red)":"var(--amber)",
                fontSize:12,padding:"3px 12px"
              }}>{weeklySummary.overallStatus==="bun"?t("Bun"):weeklySummary.overallStatus==="ingrijorator"?t("Îngrijorător"):t("Atenție")}</span>
            )}
          </div>
          {weeklySummary.adherenceSummary&&(
            <div className="card" style={{border:"1px solid var(--blue-line)"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <div className="lbl" style={{color:"var(--blue)",margin:0}}>{t("💊 Respectarea tratamentului")}</div>
                {weeklySummary.adherencePercent!=null&&<span style={{fontSize:16,fontWeight:700,color:weeklySummary.adherencePercent>=80?"var(--green)":weeklySummary.adherencePercent>=50?"var(--amber)":"var(--red)"}}>{weeklySummary.adherencePercent}%</span>}
              </div>
              <div style={{fontSize:13,lineHeight:1.6}}>{toText(weeklySummary.adherenceSummary)}</div>
              {weeklySummary.adherenceChange&&<div style={{marginTop:6,fontSize:12,color:"var(--text-3)",fontStyle:"italic"}}>↕ {toText(weeklySummary.adherenceChange)}</div>}
              {weeklySummary.missedMeds?.length>0&&<div style={{marginTop:6,fontSize:12,color:"var(--red)"}}>{t("Sărite:")} {weeklySummary.missedMeds.join(", ")}</div>}
            </div>
          )}
          {weeklySummary.medicationChanges?.length>0&&(
            <div className="card" style={{border:"1px solid #1d3a5f"}}>
              <div className="lbl" style={{color:"var(--blue-2)",marginBottom:8}}>{t("↕ Schimbări față de perioada precedentă")}</div>
              {(weeklySummary.medicationChanges||[]).map((item,i)=>(
                <div key={i} style={{display:"flex",gap:10,padding:"6px 0",borderBottom:i<weeklySummary.medicationChanges.length-1?"1px solid var(--border)":"none",fontSize:13,lineHeight:1.6}}>
                  <span style={{color:"var(--blue-2)",flexShrink:0}}>↕</span><span>{item}</span>
                </div>
              ))}
            </div>
          )}
          {weeklySummary.improvements?.length>0&&(
            <div className="card" style={{border:"1px solid var(--green-line)"}}>
              <div className="lbl" style={{color:"var(--green)",marginBottom:8}}>{t("↑ Îmbunătățiri față de perioada precedentă")}</div>
              {(weeklySummary.improvements||[]).map((item,i)=>(
                <div key={i} style={{display:"flex",gap:10,padding:"6px 0",borderBottom:i<weeklySummary.improvements.length-1?"1px solid var(--border)":"none",fontSize:13,lineHeight:1.6}}>
                  <span style={{color:"var(--green)",flexShrink:0}}>↑</span><span>{item}</span>
                </div>
              ))}
            </div>
          )}
          {weeklySummary.symptomsSummary&&<div className="card"><div className="lbl" style={{color:"var(--purple)",marginBottom:6}}>{t("🤒 Simptome")}</div><div style={{fontSize:13,lineHeight:1.6}}>{toText(weeklySummary.symptomsSummary)}</div></div>}
          {weeklySummary.metricsTrend&&<div className="card"><div className="lbl" style={{color:"var(--amber)",marginBottom:6}}>{t("📊 Valori")}</div><div style={{fontSize:13,lineHeight:1.6}}>{toText(weeklySummary.metricsTrend)}</div></div>}
          {weeklySummary.concerningPatterns?.length>0&&(
            <div className="card" style={{border:"1px solid var(--red-line)"}}>
              <div className="lbl" style={{color:"var(--red)",marginBottom:8}}>{t("⚠ Aspecte")}</div>
              {(weeklySummary.concerningPatterns||[]).map((item,i)=>(
                <div key={i} style={{display:"flex",gap:10,padding:"6px 0",borderBottom:i<weeklySummary.concerningPatterns.length-1?"1px solid var(--border)":"none",fontSize:13,lineHeight:1.6}}>
                  <span style={{color:"var(--red)",flexShrink:0}}>!</span><span style={{color:"var(--red-2)"}}>{item}</span>
                </div>
              ))}
            </div>
          )}
          {weeklySummary.recommendations?.length>0&&(
            <div className="card" style={{border:"1px solid var(--green-line)"}}>
              <div className="lbl" style={{color:"var(--green)",marginBottom:8}}>{t("✓ Recomandări")}</div>
              {(weeklySummary.recommendations||[]).map((item,i)=>(
                <div key={i} style={{display:"flex",gap:10,padding:"6px 0",borderBottom:i<weeklySummary.recommendations.length-1?"1px solid var(--border)":"none",fontSize:13,lineHeight:1.6}}>
                  <span style={{color:"var(--green)",flexShrink:0}}>✓</span><span>{item}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


export function SumarAITab({aiSummary,weeklySummary,annualSummary,onRefresh,onWeekly,onAnnual,onLoadAnnualYear,analysisDocs,symptoms,processing,meds,medLogs,metrics,docs,events}) {
  const [annualYear,setAnnualYear] = useState(new Date().getFullYear());
  // La schimbarea anului, încarcă sumarul salvat pentru acel an
  const [activeSection, setActiveSection] = useState(window._sumarSection||"general");
  React.useEffect(()=>{ window._sumarSection = activeSection; }, [activeSection]);
  // La schimbarea anului, încarcă sumarul salvat pentru acel an (dar nu la prima montare dacă e anul curent deja încărcat)
  const annualLoadedRef = React.useRef(false);
  React.useEffect(()=>{
    if(!annualLoadedRef.current){ annualLoadedRef.current=true; return; }
    if(onLoadAnnualYear) onLoadAnnualYear(annualYear);
    // Intenționat doar pe `annualYear`: încărcăm sumarul salvat la schimbarea anului (sărind prima montare).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annualYear]);
  const year = new Date().getFullYear();

  function StatusBadge({status}) {
    const cfg = {bun:{bg:"var(--green-bg-soft)",color:"var(--green)",label:"Bun"}, atentie:{bg:"var(--amber-bg)",color:"var(--amber)",label:"Atenție"}, ingrijorator:{bg:"var(--red-bg-soft)",color:"var(--red)",label:"Îngrijorător"}};
    const c = cfg[status]||cfg["atentie"];
    return <span className="pill" style={{background:c.bg,color:c.color,fontSize:12,padding:"3px 12px"}}>{t(c.label)}</span>;
  }

  function SectionList({items, color, icon}) {
    const list = asStrArr(items); // AI-ul poate întoarce string, obiect sau listă mixtă
    if(!list.length) return null;
    return (
      <div>
        {list.map((item,i)=>(
          <div key={i} style={{display:"flex",gap:10,padding:"7px 0",borderBottom:i<list.length-1?"1px solid var(--border)":"none"}}>
            <span style={{color,flexShrink:0,fontSize:12}}>{icon||"●"}</span>
            <div style={{fontSize:13,lineHeight:1.6,color:color==="#f87171"?"var(--red-2)":color==="#a78bfa"?"var(--purple-2)":"var(--text)"}}>{item}</div>
          </div>
        ))}
      </div>
    );
  }

  // Export sumar curent ca PDF (fereastră print)
  function exportSummaryPDF(title, summaryObj) {
    if(!summaryObj) { fmAlert(t("Nu există un sumar generat de exportat.")); return; }
    const sections=[];
    const push=(label,val)=>{ if(!val) return; if(Array.isArray(val)){ if(!val.length) return; sections.push(`<h3>${label}</h3><ul>${val.map(x=>`<li>${escapeHtml(x)}</li>`).join("")}</ul>`);} else sections.push(`<h3>${label}</h3><p>${escapeHtml(val)}</p>`); };
    push(t("Rezumat"), summaryObj.summary||summaryObj.overallProgress||summaryObj.adherenceSummary);
    push(t("Observații"), summaryObj.insights);
    push(t("Respectarea tratamentului"), summaryObj.adherenceSummary);
    push(t("Schimbări medicație"), summaryObj.medicationChanges||summaryObj.medicationNotes);
    push(t("Simptome"), summaryObj.symptomsSummary);
    push(t("Tendințe valori"), summaryObj.metricsTrend||summaryObj.metricsEvolution);
    push(t("Evoluție diagnostice"), summaryObj.conditionsEvolution);
    push(t("Avertismente"), summaryObj.warnings||summaryObj.concerningPatterns);
    push(t("Îmbunătățiri"), summaryObj.improvements);
    push(t("Îngrijorări"), summaryObj.concerns);
    push(t("Evenimente cheie"), summaryObj.keyEvents);
    push(t("Recomandări"), summaryObj.recommendations||summaryObj.recommendationsNextYear||summaryObj.familyRiskRecommendations);
    const html=`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title>
      <style>body{font-family:Arial,sans-serif;padding:24px;color:#222;max-width:800px;margin:0 auto}
      h1{font-size:20px;margin-bottom:4px}h3{font-size:14px;color:#7c3aed;margin:16px 0 4px}
      .sub{color:#666;font-size:12px;margin-bottom:16px}p{font-size:13px;line-height:1.6}
      ul{margin:4px 0}li{font-size:13px;line-height:1.6;margin-bottom:3px}
      .disc{margin-top:24px;padding:12px;background:#f5f5f5;border-radius:8px;font-size:11px;color:#666}
      @media print{body{padding:0}}</style></head>
      <body><h1>${title}</h1><div class="sub">${t("Generat:")} ${summaryObj.generatedAt||new Date().toLocaleString("ro-RO")}</div>
      ${sections.join("")}
      <div class="disc">${t("Acest sumar este generat automat de inteligență artificială și are rol orientativ. Nu înlocuiește consultul, diagnosticul sau interpretarea unui medic.")}</div>
      <script>window.onload=()=>window.print();</script></body></html>`;
    const w=window.open("","_blank");
    if(w){ w.document.write(html); w.document.close(); }
    else fmAlert(t("Deblochează pop-up-urile pentru a exporta PDF."));
  }

  return (
    <div>
      {/* Section tabs */}
      <div className="subtabs" style={{marginBottom:16}}>
        {[["general","✦ "+t("General")],["weekly","📅 "+t("Săptămânal")],["annual","📆 "+t("Anual")]].map(([k,l])=>(
          <button key={k} className={`stab${activeSection===k?" act":""}`} onClick={()=>setActiveSection(k)}>{l}</button>
        ))}
      </div>

      {/* GENERAL */}
      {activeSection==="general"&&(
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <div className="sec" style={{margin:0}}>{t("Starea actuală de sănătate")}</div>
            <div style={{display:"flex",gap:6}}>
              {aiSummary&&<button className="bg" style={{fontSize:11,padding:"6px 10px"}} onClick={()=>exportSummaryPDF(t("Sumar general de sănătate"),aiSummary)}>{t("⬇ PDF")}</button>}
              <button className="bai" onClick={onRefresh} disabled={processing}>✦ {processing?t("Generez..."):t("Actualizează")}</button>
            </div>
          </div>
          {!aiSummary?(
            <div className="card" style={{textAlign:"center",color:"var(--text-4)",padding:"48px 0"}}>
              <div style={{fontSize:40,marginBottom:8}}>✦</div>
              <div style={{marginBottom:14}}>{t("Adaugă date medicale pentru a genera sumarul.")}</div>
              <button className="bai" onClick={onRefresh}>{t("✦ Generează acum")}</button>
            </div>
          ):(
            <div>
              <div style={{fontSize:11,color:"var(--text-4)",marginBottom:12,textAlign:"right"}}>{t("Generat:")} {aiSummary.generatedAt}</div>
              {aiSummary.summary&&<div className="card" style={{border:"1px solid var(--blue-line)"}}><div className="lbl" style={{color:"var(--blue)"}}>{t("Rezumat")}</div><div style={{fontSize:14,lineHeight:1.7,marginTop:4}}>{toText(aiSummary.summary)}</div></div>}
              {aiSummary.insights?.length>0&&<div className="card"><div className="lbl" style={{color:"var(--green)",marginBottom:8}}>{t("Observații")}</div><SectionList items={aiSummary.insights} color="#34d399"/></div>}
              {aiSummary.medicationNotes?.length>0&&<div className="card" style={{border:"1px solid var(--blue-line)"}}><div className="lbl" style={{color:"var(--blue)",marginBottom:8}}>{t("💊 Notițe medicamente")}</div><SectionList items={aiSummary.medicationNotes} color="#93c5fd"/></div>}
              {aiSummary.warnings?.length>0&&<div className="card" style={{border:"1px solid var(--red-line)"}}><div className="lbl" style={{color:"var(--red)",marginBottom:8}}>{t("⚠ Aspecte de urmărit")}</div><SectionList items={aiSummary.warnings} color="#f87171" icon="!"/></div>}
            </div>
          )}
        </div>
      )}

      {/* WEEKLY */}
      {activeSection==="weekly"&&(
        <div>
          <WeeklySummarySection
            weeklySummary={weeklySummary}
            onGenerate={onWeekly}
            processing={processing}
            onExport={exportSummaryPDF}
          />
        </div>
      )}

      {/* ANNUAL */}
      {activeSection==="annual"&&(()=>{
        // Anii disponibili — din toate datele înregistrate
        const yearsSet = new Set();
        docs.forEach(d=>d.date&&yearsSet.add(d.date.slice(0,4)));
        metrics.forEach(m=>m.date&&yearsSet.add(m.date.slice(0,4)));
        analysisDocs.forEach(a=>a.date&&yearsSet.add(a.date.slice(0,4)));
        symptoms.forEach(s=>s.date&&yearsSet.add(s.date.slice(0,4)));
        yearsSet.add(String(year));
        const availableYears = [...yearsSet].sort((a,b)=>b.localeCompare(a));
        return (
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,gap:8,flexWrap:"wrap"}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <div className="sec" style={{margin:0}}>{t("Evoluție")}</div>
              <div style={{width:110}}><Dropdown value={annualYear} onChange={v=>setAnnualYear(+v)} options={availableYears.map(y=>({v:y,l:String(y)}))}/></div>
            </div>
            <div style={{display:"flex",gap:6}}>
              {annualSummary&&<button className="bg" style={{fontSize:11,padding:"6px 10px"}} onClick={()=>exportSummaryPDF(t("Sumar anual")+" "+annualYear,annualSummary)}>{t("⬇ PDF")}</button>}
              <button className="bai" onClick={()=>onAnnual(annualYear)} disabled={processing}>📆 {processing?t("Generez..."):annualSummary?t("Actualizează"):t("Generează")}</button>
            </div>
          </div>
          {!annualSummary?(
            <div className="card" style={{textAlign:"center",color:"var(--text-4)",padding:"48px 0"}}>
              <div style={{fontSize:40,marginBottom:8}}>📆</div>
              <div style={{marginBottom:14}}>{t("Apasă „Generează” pentru sumarul anului")} {annualYear}.</div>
              <button className="bai" onClick={()=>onAnnual(annualYear)}>{t("📆 Generează acum")}</button>
            </div>
          ):(
            <div>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
                <div style={{fontSize:11,color:"var(--text-4)"}}>{t("Anul")} {annualSummary.year} · {t("Generat:")} {annualSummary.generatedAt}</div>
                <StatusBadge status={annualSummary.overallStatus}/>
              </div>
              {annualSummary.overallProgress&&<div className="card" style={{border:"1px solid var(--blue-line)"}}><div className="lbl" style={{color:"var(--blue)"}}>{t("Progres general")}</div><div style={{fontSize:14,lineHeight:1.7,marginTop:4}}>{toText(annualSummary.overallProgress)}</div></div>}
              {annualSummary.keyEvents?.length>0&&<div className="card"><div className="lbl" style={{color:"var(--amber)",marginBottom:8}}>{t("📌 Evenimente cheie")}</div><SectionList items={annualSummary.keyEvents} color="#fbbf24" icon="📌"/></div>}
              {annualSummary.conditionsEvolution&&<div className="card"><div className="lbl" style={{color:"var(--purple)",marginBottom:6}}>{t("🏥 Evoluție diagnostice")}</div><div style={{fontSize:13,lineHeight:1.6}}>{toText(annualSummary.conditionsEvolution)}</div></div>}
              {annualSummary.metricsEvolution&&<div className="card"><div className="lbl" style={{color:"var(--green)",marginBottom:6}}>{t("📊 Evoluție valori")}</div><div style={{fontSize:13,lineHeight:1.6}}>{toText(annualSummary.metricsEvolution)}</div></div>}
              {annualSummary.medicationChanges&&<div className="card"><div className="lbl" style={{color:"var(--blue)",marginBottom:6}}>{t("💊 Schimbări tratament")}</div><div style={{fontSize:13,lineHeight:1.6}}>{toText(annualSummary.medicationChanges)}</div></div>}
              {annualSummary.improvements?.length>0&&<div className="card" style={{border:"1px solid var(--green-line)"}}><div className="lbl" style={{color:"var(--green)",marginBottom:8}}>{t("✓ Îmbunătățiri")}</div><SectionList items={annualSummary.improvements} color="#34d399" icon="✓"/></div>}
              {annualSummary.concerns?.length>0&&<div className="card" style={{border:"1px solid var(--red-line)"}}><div className="lbl" style={{color:"var(--red)",marginBottom:8}}>{t("⚠ Îngrijorări")}</div><SectionList items={annualSummary.concerns} color="#f87171" icon="!"/></div>}
              {annualSummary.recommendationsNextYear?.length>0&&<div className="card" style={{border:"1px solid var(--purple-line)"}}><div className="lbl" style={{color:"var(--purple)",marginBottom:8}}>✦ {t("Recomandări")} {annualYear+1}</div><SectionList items={annualSummary.recommendationsNextYear} color="#a78bfa" icon="✦"/></div>}
            </div>
          )}
        </div>
        );
      })()}
    </div>
  );
}

